import React, { useState, useCallback } from 'react';

interface OptimizedLogoProps {
    /** Nome do arquivo da logo (sem extensão) */
    name: string;
    alt: string;
    className?: string;
    style?: React.CSSProperties;
    loading?: 'lazy' | 'eager';
    /** Se deve usar a versão original (não otimizada) como fallback final */
    useOriginalFallback?: boolean;
    onLoad?: () => void;
    onError?: () => void;
}

/**
 * Componente para exibir logos otimizadas com suporte a WebP e fallback para PNG
 * As logos otimizadas ficam em /assets/logos/optimized/
 */
const OptimizedLogo: React.FC<OptimizedLogoProps> = ({
    name,
    alt,
    className,
    style,
    loading = 'eager',
    useOriginalFallback = true,
    onLoad,
    onError
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const basePath = '/assets/logos/optimized';
    const originalPath = '/assets/logos';

    const handleLoad = useCallback(() => {
        setIsLoading(false);
        setHasError(false);
        onLoad?.();
    }, [onLoad]);

    const handleError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.target as HTMLImageElement;

        // Tentar fallback para versão original se habilitado
        if (useOriginalFallback && !img.src.includes(originalPath)) {
            img.src = `${originalPath}/${name}.png`;
            return;
        }

        setIsLoading(false);
        setHasError(true);
        onError?.();
    }, [name, originalPath, useOriginalFallback, onError]);

    if (hasError) {
        return (
            <div
                className={className}
                style={{
                    ...style,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(100, 100, 100, 0.1)',
                    color: '#666',
                    fontSize: '0.75rem'
                }}
            >
                Logo
            </div>
        );
    }

    return (
        <picture className={className} style={style}>
            {/* WebP para navegadores modernos */}
            <source
                srcSet={`${basePath}/${name}.webp`}
                type="image/webp"
            />
            {/* PNG otimizado como fallback */}
            <source
                srcSet={`${basePath}/${name}.png`}
                type="image/png"
            />
            {/* Fallback img para navegadores antigos */}
            <img
                src={`${basePath}/${name}.png`}
                alt={alt}
                loading={loading}
                onLoad={handleLoad}
                onError={handleError}
                className={className}
                style={{
                    ...style,
                    opacity: isLoading ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out'
                }}
                decoding="async"
            />
        </picture>
    );
};

export default OptimizedLogo;
